import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShieldCheck, ShieldX, Loader2, QrCode } from 'lucide-react';
import api from '../services/api';
import { Card, Tag } from '../components/sv';
import { formatDateTime } from '../lib/utils';

// Public verification of the signature ID / QR printed on Südvik and exporter documents.
export default function VerifyPage() {
  const { sigId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(function () {
    setLoading(true);
    api.get('/documents/verify/' + encodeURIComponent(sigId)).then(function (r) { setData(r.data); }).catch(function () { setData({ valid: false, signature_id: sigId }); }).finally(function () { setLoading(false); });
  }, [sigId]);

  return (
    <div className="bg-gray-50 min-h-screen" data-testid="verify-page">
      <div className="bg-white border-b border-gray-200"><div className="sv-wrap py-10 md:py-12"><h1 className="text-[36px] md:text-[40px] sv-display">Document verification</h1><p className="text-gray-700 mt-2 max-w-[640px]">Every document issued through Südvik carries a signature ID and QR code. Scan or enter it to confirm who signed the document, when, and which issued versions exist.</p></div></div>
      <div className="sv-wrap py-8 pb-24">
        {loading ? <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-500" /></div> : data && data.valid ? (
          <Card className="max-w-[760px]" data-testid="verify-valid">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-success-100 text-success-700 grid place-items-center shrink-0"><ShieldCheck className="h-6 w-6" strokeWidth={1.75} /></div>
              <div className="flex-1">
                <p className="text-xl">Signature verified</p>
                <p className="text-gray-700 mt-1">{data.kind === 'electronic' ? 'Electronically signed' : 'Digitally signed'} by <span className="text-ink font-medium" data-testid="verify-signer">{data.signer}</span> on {formatDateTime(data.signed_at)}.</p>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mt-6 text-[15px]">
                  {[['Document', data.title], ['Number', data.number], ['Signature ID', data.signature_id], ['Issuer', data.issuer_kind === 'carrier' ? 'Südvik Line A/S (carrier)' : 'Exporter — via Südvik Cargo Documents'], ['Booking', data.booking_number], ['Bill of lading', data.bl_number || '—'], ['Vessel / voyage', (data.vessel || '—') + (data.voyage ? ' · ' + data.voyage : '')], ['Route', data.route]].map(function (r) {
                    return <div key={r[0]}><div className="text-sm text-gray-600">{r[0]}</div><div className={'mt-0.5 ' + (['Number', 'Signature ID', 'Booking', 'Bill of lading'].includes(r[0]) ? 'sv-mono' : '')} data-testid={'verify-' + r[0].toLowerCase().replace(/[^a-z]+/g, '-')}>{r[1]}</div></div>;
                  })}
                </div>
                <div className="mt-6 pt-5 border-t border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">Issued versions on file {data.versions.length === 0 && '— none archived yet'}</p>
                  <ul className="space-y-1.5" data-testid="verify-versions">
                    {data.versions.map(function (v) { return <li key={v.version} className="flex flex-wrap items-center gap-3 text-sm"><Tag mono>v{v.version}</Tag><span>{formatDateTime(v.issued_at)}</span><span className="sv-mono text-gray-600 text-xs">SHA-256 {v.fingerprint}…</span>{v.emailed && <Tag>Emailed</Tag>}</li>; })}
                  </ul>
                  <p className="text-xs text-gray-500 mt-4">Compare the fingerprint with the SHA-256 of the file you received to confirm it is an unaltered issued copy. Contact documentation@sudvik.com if the details do not match your document.</p>
                </div>
              </div>
            </div>
          </Card>
        ) : (
          <Card className="max-w-[760px]" data-testid="verify-invalid">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-red-50 text-red-700 grid place-items-center shrink-0"><ShieldX className="h-6 w-6" strokeWidth={1.75} /></div>
              <div>
                <p className="text-xl">No document matches this signature ID</p>
                <p className="text-gray-700 mt-1">ID <span className="sv-mono">{sigId}</span> is not registered. The document may be a draft, altered, or not issued through Südvik.</p>
                <p className="text-sm text-gray-600 mt-4 flex items-center gap-2"><QrCode className="h-4 w-4" /> Scan the QR code on the document, or <Link to="/track" className="sv-link">track the shipment</Link> instead.</p>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
